import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import systemConfigService from '../services/SystemConfigService';
import Loading from '../components/loading/Loading';
import { AUTH_CHANGED_EVENT, EndLocalSession } from '../api/API';
import { getAccessToken } from '../api/authTokens';

// Create context
const SystemConfigContext = createContext();

/**
 * SystemConfigProvider
 * 
 * Context provider for system configuration.
 * Makes system configuration available throughout the application.
 */
export const SystemConfigProvider = ({ children }) => {
  const [config, setConfig] = useState(null);
  const [publicConfig, setPublicConfig] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check if user is authenticated
  useEffect(() => {
    const checkAuth = () => {
      setIsAuthenticated(!!getAccessToken());
    };

    // Initial check
    checkAuth();

    // O access token fica só em memória, por aba (L-21): não há token a sincronizar.
    // Quando outra aba encerra a sessão ela apaga sessionData (key null =
    // localStorage.clear()); esta aba também encerra a sua, só localmente.
    const handleStorage = (event) => {
      if (event.key === null || (event.key === 'sessionData' && event.newValue === null)) {
        EndLocalSession();
      }
    };

    // Login ou Disconnect nesta aba (disparado por API.js)
    window.addEventListener('storage', handleStorage);
    window.addEventListener(AUTH_CHANGED_EVENT, checkAuth);

    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener(AUTH_CHANGED_EVENT, checkAuth);
    };
  }, []);

  // Load public system configuration on mount
  useEffect(() => {
    const loadPublicConfig = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const fallbackTimeout = setTimeout(() => {
          setPublicConfig({ product: { name: 'Gabriela' }, settings: {}, subscription: { plans: [], addons: [] } });
          setConfig({ product: { name: 'Gabriela' }, settings: {}, subscription: { plans: [], addons: [] } });
          setIsLoading(false);
        }, 8000);

        const publicConfigData = await systemConfigService.fetchPublicConfig();
        clearTimeout(fallbackTimeout);
        setPublicConfig(publicConfigData);
        setConfig(publicConfigData);
        
        window.dispatchEvent(new CustomEvent('publicConfigLoaded', { detail: publicConfigData }));
      } catch (err) {
        setError(err.toString());
        setPublicConfig({ product: { name: 'Gabriela' }, settings: {}, subscription: { plans: [], addons: [] } });
        setConfig({ product: { name: 'Gabriela' }, settings: {}, subscription: { plans: [], addons: [] } });
      } finally {
        setIsLoading(false);
      }
    };

    loadPublicConfig();
  }, []);

  // Load private system configuration when authenticated - with retry mechanism.
  // Não liga isLoading: recarregar a configuração não pode desmontar o app
  // (o loading de tela cheia só aparece enquanto ainda não há config).
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    let cancelled = false;
    let retryTimer = null;
    let retryCount = 0;
    const maxRetries = 3;

    const loadPrivateConfig = async () => {
      if (cancelled) {
        return;
      }

      try {
        const configData = await systemConfigService.fetchConfig();
        if (cancelled) {
          return;
        }
        setConfig(configData);
        setIsLoading(false);

        // Dispatch event for testing
        window.dispatchEvent(new CustomEvent('privateConfigLoaded', { detail: configData }));
      } catch (err) {
        if (cancelled) {
          return;
        }

        // Sessão encerrada: tentar de novo só geraria novos 401
        const sessionExpired = err?.code === 'session_expired' || !getAccessToken();

        // Retry logic
        if (!sessionExpired && retryCount < maxRetries) {
          retryCount++;
          retryTimer = setTimeout(loadPrivateConfig, 1000); // Wait 1 second before retry
        } else {
          setIsLoading(false);
        }
      }
    };

    loadPrivateConfig();

    // Autenticação mudou (ou desmontou): cancela tentativas pendentes
    return () => {
      cancelled = true;
      clearTimeout(retryTimer);
    };
  }, [isAuthenticated]);

  /**
   * Check if a feature is enabled
   * @param {string} featureKey - The feature key to check
   * @returns {boolean} True if the feature is enabled, false otherwise
   */
  const isFeatureEnabled = (featureKey) => {
    if (!config || !config.features) {
      return false;
    }
    
    return !!config.features[featureKey];
  };

  /**
   * Get a system setting value
   * @param {string} settingKey - The setting key to get
   * @param {*} defaultValue - Default value to return if setting is not found
   * @returns {*} The setting value or defaultValue if not found
   */
  const getSetting = (settingKey, defaultValue = null) => {
    if (!config || !config.settings) {
      return defaultValue;
    }
    
    return config.settings[settingKey] != undefined 
      ? config.settings[settingKey] 
      : defaultValue;
  };

  /**
   * Get default message template by key
   * @param {string} templateKey - The template key to get
   * @returns {string|null} The template content or null if not found
   */
  const getDefaultMessageTemplate = (templateKey) => {
    if (!config || !config.defaultTemplates || !config.defaultTemplates.messages) {
      return null;
    }
    
    const template = config.defaultTemplates.messages.find(t => t.key == templateKey);
    return template ? template.content : null;
  };

  /**
   * Get product information
   * @returns {Object|null} Product information or null if not loaded
   */
  const getProductInfo = () => {
    if (!config || !config.product) {
      return null;
    }
    
    return config.product;
  };

  /**
   * Update system configuration - REMOVED
   * System configuration is now read-only and can only be modified by direct server-side changes.
   */
  const updateConfig = async () => {
    
    throw new Error('System configuration is read-only');
  };

  /**
   * Força um novo fetch das configurações privadas da empresa.
   * Útil após operações que alteram dados da empresa (ex: finalização do Setup Wizard).
   * O reload acontece em background — não exibe loading global.
   */
  const refreshConfig = useCallback(async () => {
    if (!isAuthenticated) {
      return;
    }

    try {
      const configData = await systemConfigService.fetchConfig();
      setConfig(configData);

      // Disparar evento para possíveis listeners
      window.dispatchEvent(new CustomEvent('privateConfigLoaded', { detail: configData }));
    } catch (err) {
      // Falha silenciosa — não interrompe o fluxo do usuário
    }
  }, [isAuthenticated]);

  // Context value
  const value = {
    config,
    publicConfig,
    isLoading,
    error,
    isAuthenticated,
    isFeatureEnabled,
    getSetting,
    getDefaultMessageTemplate,
    getProductInfo,
    updateConfig,
    refreshConfig
  };

  // Only render children when configuration is loaded
  return (
    <SystemConfigContext.Provider value={value}>
      {isLoading && !config ? (
        // Show loading indicator only on the first load (no configuration yet)
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          width: '100vw'
        }}>
          <Loading color="secondary" />
          {error && (
            <div style={{ color: 'red', marginTop: '20px' }}>
              Error loading configuration: {error}
            </div>
          )}
        </div>
      ) : (
        // Only render the application when configuration is loaded
        children
      )}
    </SystemConfigContext.Provider>
  );
};

/**
 * useSystemConfig hook
 * 
 * Hook to access system configuration from any component.
 * @returns {Object} System configuration context
 */
export const useSystemConfig = () => {
  const context = useContext(SystemConfigContext);
  
  if (context == undefined) {
    throw new Error('useSystemConfig must be used within a SystemConfigProvider');
  }
  
  return context;
};